// @ts-check
// Anfitriões da página de agendamento (contrato 10 de agendamento, seções 5.2 e 7.2): quem recebe, a conta Google de
// cada um, a agenda de disponibilidade (na fonte "Agenda do Google") e as agendas que também contam como ocupado. Quem
// não pode receber aparece com o motivo (sem conta, reconectar, só Microsoft) e, desde a 3.5.0, com o botão que resolve.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { BOOKING_LIMITS } from '../../../../shared/booking/booking-values.js';
import { listHostCalendars } from '../../data/booking-pages.js';
import { hostAction, hostIssue } from '../../lib/booking-pages.js';
import { Link } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/booking-pages.js').BookingHost} BookingHost
 * @typedef {import('../../data/booking-pages.js').GoogleCalendar} GoogleCalendar
 * @typedef {import('../../../../shared/booking/booking-values.js').BookingHostInput} BookingHostInput
 */

/**
 * Agendas da conta Google de um anfitrião, carregadas uma vez.
 * @param {{ host: BookingHost, value: BookingHostInput, availabilitySource: string, disabled: boolean, onChange: (value: BookingHostInput) => void }} props
 */
function HostCalendars({ host, value, availabilitySource, disabled, onChange }) {
  const [calendars, setCalendars] = useState(/** @type {GoogleCalendar[] | null} */ (null));
  const [hasError, setHasError] = useState(false);
  const googleAccounts = host.accounts.filter((account) => account.provider === 'GOOGLE');
  useEffect(() => {
    let isCurrent = true;
    setCalendars(null);
    setHasError(false);
    listHostCalendars(host.workspaceMemberId, value.connectedAccountId)
      .then((list) => isCurrent && setCalendars(list))
      .catch(() => {
        if (!isCurrent) return;
        setHasError(true);
        setCalendars([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [host.workspaceMemberId, value.connectedAccountId]);
  const secondary = (calendars ?? []).filter((calendar) => !calendar.isPrimary);
  const selectId = `booking-host-availability-${host.workspaceMemberId}`;
  return html`<div class="booking-host-calendars">
    ${googleAccounts.length > 1
      ? html`<label class="form-field">
          <span class="form-field-label">${t('bookingPages.hosts.account')}</span>
          <select class="text-input" value=${value.connectedAccountId ?? ''} disabled=${disabled}
            onChange=${(/** @type {Event} */ event) => onChange({ ...value, connectedAccountId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null, availabilityCalendarId: null, busyCalendarIds: [] })}>
            ${googleAccounts.map((account) => html`<option key=${account.connectedAccountId} value=${account.connectedAccountId}>${account.handle}</option>`)}
          </select>
        </label>`
      : null}
    ${availabilitySource === 'GOOGLE_CALENDAR'
      ? html`<label class="form-field" for=${selectId}>
          <span class="form-field-label">${t('bookingPages.hosts.availabilityCalendar')}</span>
          <select id=${selectId} class="text-input" value=${value.availabilityCalendarId ?? ''} disabled=${disabled || calendars === null}
            onChange=${(/** @type {Event} */ event) => {
              const availabilityCalendarId = /** @type {HTMLSelectElement} */ (event.currentTarget).value || null;
              onChange({ ...value, availabilityCalendarId, busyCalendarIds: value.busyCalendarIds.filter((id) => id !== availabilityCalendarId) });
            }}>
            <option value="">${calendars === null ? t('bookingPages.hosts.loadingCalendars') : t('bookingPages.hosts.chooseCalendar')}</option>
            ${secondary.map((calendar) => html`<option key=${calendar.id} value=${calendar.id}>${calendar.name}</option>`)}
          </select>
          <span class="input-hint">${t('bookingPages.hosts.availabilityHint')}</span>
        </label>`
      : null}
    ${hasError ? html`<span class="input-hint booking-warning"><${Icon} name="IconAlertTriangle" size=${14} />${t('bookingPages.hosts.calendarsUnavailable')}</span>` : null}
    ${secondary.filter((calendar) => calendar.id !== value.availabilityCalendarId).length > 0
      ? html`<fieldset class="booking-busy-calendars">
          <legend class="form-field-label">${t('bookingPages.hosts.busyCalendars')}</legend>
          <span class="input-hint">${t('bookingPages.hosts.busyHint')}</span>
          ${secondary.filter((calendar) => calendar.id !== value.availabilityCalendarId).map((calendar) => {
            const isChecked = value.busyCalendarIds.includes(calendar.id);
            return html`<label key=${calendar.id} class="booking-inline-check">
              <${Checkbox} checked=${isChecked} disabled=${disabled || (!isChecked && value.busyCalendarIds.length >= BOOKING_LIMITS.busyCalendars)} ariaLabel=${calendar.name}
                onChange=${(/** @type {boolean} */ checked) => onChange({ ...value, busyCalendarIds: checked ? [...value.busyCalendarIds, calendar.id] : value.busyCalendarIds.filter((id) => id !== calendar.id) })} />
              <span>${calendar.name}</span>
            </label>`;
          })}
        </fieldset>`
      : null}
  </div>`;
}

/**
 * Lista de quem recebe. Quem ainda não pode (sem conta Google, sem o aceite, sem a permissão de criar eventos) também
 * pode ser marcado: fica pendente na página (3.5.0) e entra na agenda quando resolver. O botão ao lado resolve a
 * pendência: para a própria pessoa, aceitar e permitir criar eventos num clique; para outro membro, o convite por aviso
 * e e-mail.
 * @param {{ hosts: BookingHost[] | null, value: BookingHostInput[], availabilitySource: string, disabled?: boolean, invalidMemberIds?: string[],
 *   currentMemberId?: string | null, busyMemberId?: string | null, onChange: (value: BookingHostInput[]) => void,
 *   onResolve?: (host: BookingHost, action: 'ACCEPT' | 'CONNECT' | 'INVITE') => void }} props
 */
export function BookingHostPicker({ hosts, value, availabilitySource, disabled = false, invalidMemberIds = [], currentMemberId = null, busyMemberId = null, onChange, onResolve }) {
  if (hosts === null) return html`<p class="input-hint">${t('bookingPages.hosts.loading')}</p>`;
  if (hosts.length === 0) return html`<p class="input-hint">${t('bookingPages.hosts.empty')}</p>`;
  return html`<ul class="booking-host-list">
    ${hosts.map((host) => {
      const selected = value.find((item) => item.workspaceMemberId === host.workspaceMemberId);
      const issue = hostIssue(host, selected?.connectedAccountId ?? null);
      const eligibleAccount = host.accounts.find((account) => account.isEligible && (!selected?.connectedAccountId || account.connectedAccountId === selected.connectedAccountId));
      const isInvalid = invalidMemberIds.includes(host.workspaceMemberId);
      const isSelf = currentMemberId !== null && host.workspaceMemberId === currentMemberId;
      const action = hostAction(issue, isSelf);
      const isBusy = busyMemberId === host.workspaceMemberId;
      /** @param {boolean} checked */
      const toggle = (checked) => {
        if (!checked) {
          onChange(value.filter((item) => item.workspaceMemberId !== host.workspaceMemberId));
          return;
        }
        if (value.length >= BOOKING_LIMITS.hosts) return;
        onChange([...value, { workspaceMemberId: host.workspaceMemberId, connectedAccountId: eligibleAccount?.connectedAccountId ?? null, availabilityCalendarId: null, busyCalendarIds: [] }]);
      };
      return html`<li key=${host.workspaceMemberId} class="booking-host" data-selected=${selected ? 'true' : 'false'} data-invalid=${isInvalid ? 'true' : undefined}
        data-pending=${selected && issue ? 'true' : undefined}>
        <div class="booking-host-header">
          <${Checkbox} checked=${Boolean(selected)} disabled=${disabled} onChange=${toggle} ariaLabel=${host.name || host.email || ''} />
          <div class="booking-host-titles">
            <span class="booking-host-name">${host.name || host.email}${isSelf ? html`<span class="booking-host-self">${t('bookingPages.hosts.you')}</span>` : null}</span>
            <span class="booking-host-status" data-tone=${issue ? 'warning' : 'success'}>
              <${Icon} name=${issue ? 'IconAlertTriangle' : 'IconBrandGoogle'} size=${14} />
              ${issue ? t(`bookingPages.hostIssues.${issue}`) : t('bookingPages.hosts.googleReady', { handle: eligibleAccount?.handle ?? '' })}
            </span>
            ${selected && issue ? html`<span class="input-hint">${t('bookingPages.hosts.pendingHint')}</span>` : null}
          </div>
          ${action === 'RECONNECT'
            ? html`<${Link} href="/settings/accounts" className="booking-host-link">${t('bookingPages.hosts.openAccounts')}<//>`
            : action && onResolve && !disabled
              ? html`<${Button} title=${t(`bookingPages.hostActions.${action}`)} icon=${action === 'INVITE' ? 'IconMail' : 'IconBrandGoogle'} accent="blue" size="small"
                  isLoading=${isBusy} disabled=${busyMemberId !== null} onClick=${() => onResolve(host, action)} />`
              : null}
        </div>
        ${selected && !issue
          ? html`<${HostCalendars} host=${host} value=${selected} availabilitySource=${availabilitySource} disabled=${disabled}
              onChange=${(/** @type {BookingHostInput} */ next) => onChange(value.map((item) => (item.workspaceMemberId === host.workspaceMemberId ? next : item)))} />`
          : null}
      </li>`;
    })}
  </ul>`;
}
